import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import type { PublicDeskProjectionV1, PublicWidget } from '@shared/publicDesk'
import { WidgetBody } from './deskRenderers'
import { CaptureFrame } from './CaptureFrame'
import { safeCssColor } from './safeUrl'

// The public read-only desk canvas.
//
// It deliberately shares no code with the desktop canvas: no WidgetFrame, no
// workspace stores, no react-rnd, no window.api. Widgets are plain absolutely
// positioned divs inside one transformed world layer, which makes moving and
// resizing structurally impossible rather than disabled by a prop. The only
// state is a local camera, so nothing a viewer does can reach the owner's desk.

const MIN_ZOOM = 0.15
/** Below this, text stops being readable and a fitted desk is just a diagram. */
const LEGIBLE_ZOOM = 0.4
const MAX_ZOOM = 3

interface Camera {
  x: number
  y: number
  zoom: number
}

function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
}

export function ReadOnlyDeskCanvas({
  projection,
  apiBase,
  token
}: {
  projection: PublicDeskProjectionV1
  apiBase: string
  token: string
}): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [cam, setCam] = useState<Camera>({ x: 0, y: 0, zoom: 1 })
  const [focused, setFocused] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [linear, setLinear] = useState(false)

  const widgets = useMemo(
    () => [...projection.widgets].sort((a, b) => a.zIndex - b.zIndex),
    [projection.widgets]
  )
  const bounds = projection.bounds

  const fit = useCallback(() => {
    const host = hostRef.current
    if (!host || bounds.width === 0 || bounds.height === 0) {
      setCam({ x: 0, y: 0, zoom: 1 })
      return
    }
    const pad = 48
    const byWidth = (host.clientWidth - pad * 2) / bounds.width
    const byHeight = (host.clientHeight - pad * 2) / bounds.height
    // Fitting both dimensions puts a tall desk at 15%, where the whole board is
    // visible and not a word of it is readable -- which is the first thing a
    // recipient sees. Below the legibility floor, fit the width instead and let
    // them scroll down: seeing part of a desk you can read beats all of one you
    // cannot.
    const both = Math.min(byWidth, byHeight)
    // Three cases. The whole desk fits legibly: show all of it, centred. It
    // only fits by shrinking past readability: fit the width and start at the
    // top. Even the width is unreadable -- a very wide board -- hold the
    // legibility floor and start at the top-left corner. Seeing part of a desk
    // you can read beats all of one you cannot.
    // LEGIBLE_ZOOM is a floor, not a cap: a desk that only fits by shrinking
    // past readability is shown at the floor and panned, not shrunk further.
    const zoom = clampZoom(Math.min(Math.max(both, LEGIBLE_ZOOM), MAX_ZOOM))
    const fitsBoth = both >= LEGIBLE_ZOOM
    const fitsWidth = byWidth >= zoom
    setCam({
      x: fitsBoth || fitsWidth
        ? host.clientWidth / 2 - (bounds.x + bounds.width / 2) * zoom
        : pad - bounds.x * zoom,
      y: fitsBoth
        ? host.clientHeight / 2 - (bounds.y + bounds.height / 2) * zoom
        : pad - bounds.y * zoom,
      zoom
    })
  }, [bounds])

  // Fit once the desk is known, and whenever its extent changes materially.
  useEffect(() => {
    fit()
  }, [fit])

  const onWheel = useCallback((e: React.WheelEvent) => {
    // Cmd/Ctrl-wheel zooms about the pointer; plain wheel and trackpad pans,
    // matching the desktop canvas.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const host = hostRef.current
      if (!host) return
      const rect = host.getBoundingClientRect()
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      setCam((c) => {
        const next = clampZoom(c.zoom * (1 - e.deltaY * 0.002))
        const k = next / c.zoom
        return { zoom: next, x: px - (px - c.x) * k, y: py - (py - c.y) * k }
      })
    } else {
      setCam((c) => ({ ...c, x: c.x - e.deltaX, y: c.y - e.deltaY }))
    }
  }, [])

  // Pointer panning. No drag targets exist, so a drag anywhere pans the desk.
  const drag = useRef<{ x: number; y: number; camX: number; camY: number } | null>(null)
  const onPointerDown = (e: React.PointerEvent): void => {
    if (e.button !== 0) return
    drag.current = { x: e.clientX, y: e.clientY, camX: cam.x, camY: cam.y }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent): void => {
    const d = drag.current
    if (!d) return
    setCam((c) => ({ ...c, x: d.camX + (e.clientX - d.x), y: d.camY + (e.clientY - d.y) }))
  }
  const onPointerUp = (e: React.PointerEvent): void => {
    drag.current = null
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  }

  // Keyboard: arrows pan, +/- zoom, F fits, Escape closes focus. Bound to the
  // canvas rather than the document so it never steals keys from the page.
  const onKeyDown = (e: React.KeyboardEvent): void => {
    const step = e.shiftKey ? 200 : 60
    switch (e.key) {
      case 'ArrowLeft': setCam((c) => ({ ...c, x: c.x + step })); break
      case 'ArrowRight': setCam((c) => ({ ...c, x: c.x - step })); break
      case 'ArrowUp': setCam((c) => ({ ...c, y: c.y + step })); break
      case 'ArrowDown': setCam((c) => ({ ...c, y: c.y - step })); break
      case '+': case '=': setCam((c) => ({ ...c, zoom: clampZoom(c.zoom * 1.15) })); break
      case '-': case '_': setCam((c) => ({ ...c, zoom: clampZoom(c.zoom / 1.15) })); break
      case 'f': case 'F': fit(); break
      case 'Escape': setFocused(null); break
      default: return
    }
    e.preventDefault()
  }

  const focusedWidget = focused ? widgets.find((w) => w.id === focused) ?? null : null

  return (
    <div className="relative flex-1 min-h-0 bg-[#0b0b0c]">
      <div
        ref={hostRef}
        role="application"
        aria-label={`${projection.title} — read-only desk`}
        tabIndex={0}
        className="absolute inset-0 overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-accent/60 cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {/* One transformed world layer. Everything inside is in desk space. */}
        <div
          className="absolute top-0 left-0 origin-top-left will-change-transform"
          style={{ transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.zoom})` }}
        >
          {/* Wires, drawn in desk space beneath the widgets. */}
          <svg
            className="absolute pointer-events-none overflow-visible"
            style={{ left: bounds.x, top: bounds.y, width: Math.max(1, bounds.width), height: Math.max(1, bounds.height) }}
            aria-hidden="true"
          >
            {projection.links.map((l) => {
              const a = widgets.find((w) => w.id === l.fromWidgetId)
              const b = widgets.find((w) => w.id === l.toWidgetId)
              if (!a || !b) return null
              const ax = a.rect.x + a.rect.width / 2 - bounds.x
              const ay = a.rect.y + a.rect.height / 2 - bounds.y
              const bx = b.rect.x + b.rect.width / 2 - bounds.x
              const by = b.rect.y + b.rect.height / 2 - bounds.y
              return (
                <g key={l.id}>
                  <line x1={ax} y1={ay} x2={bx} y2={by} stroke="rgba(255,255,255,0.18)" strokeWidth={2} />
                  {l.label && (
                    <text x={(ax + bx) / 2} y={(ay + by) / 2 - 4} textAnchor="middle" fontSize={10} fill="rgba(245,245,244,0.5)">
                      {l.label}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>

          {widgets.map((w) => (
            <DeskWidget
              key={w.id}
              widget={w}
              apiBase={apiBase}
              token={token}
              selected={selected === w.id}
              onSelect={() => setSelected(w.id)}
              onOpen={() => setFocused(w.id)}
            />
          ))}
        </div>
      </div>

      <Controls
        zoom={cam.zoom}
        onZoom={(f) => setCam((c) => ({ ...c, zoom: clampZoom(c.zoom * f) }))}
        onFit={fit}
        onReset={() => setCam({ x: 0, y: 0, zoom: 1 })}
        linear={linear}
        onToggleLinear={() => setLinear((v) => !v)}
      />

      <Minimap bounds={bounds} widgets={widgets} cam={cam} host={hostRef} onJump={setCam} />

      {/* A linear, screen-reader friendly reading of the same desk. A spatial
          canvas is not navigable by assistive technology on its own. */}
      {linear && <LinearView projection={projection} apiBase={apiBase} token={token} onClose={() => setLinear(false)} />}

      {focusedWidget && (
        <FocusView widget={focusedWidget} apiBase={apiBase} token={token} onClose={() => setFocused(null)} />
      )}
    </div>
  )
}

function DeskWidget({
  widget,
  apiBase,
  token,
  selected,
  onSelect,
  onOpen
}: {
  widget: PublicWidget
  apiBase: string
  token: string
  selected: boolean
  onSelect: () => void
  onOpen: () => void
}): JSX.Element {
  const isSection = widget.render.type === 'section'
  return (
    <div
      className={`absolute rounded-lg border ${
        selected ? 'border-accent/70' : 'border-white/10'
      } ${isSection ? 'bg-white/[0.02]' : 'bg-stone-900/80'} overflow-hidden shadow-lg`}
      style={{
        left: widget.rect.x,
        top: widget.rect.y,
        width: widget.rect.width,
        height: widget.rect.height,
        zIndex: widget.zIndex,
        // The widget's own colour is shared content: a colour or nothing.
        borderColor: selected ? undefined : (safeCssColor(widget.color) ?? undefined)
      }}
      // Selecting brings a window visually forward for this viewer only; it is
      // never persisted anywhere.
      onPointerDown={(e) => { e.stopPropagation(); onSelect() }}
      onDoubleClick={onOpen}
    >
      {!isSection && (
        <div className="flex items-center justify-between gap-2 px-2 py-1 border-b border-white/10 bg-white/[0.03]">
          <span className="text-[11px] truncate text-stone-300">{widget.title || widget.kind}</span>
          <button
            className="text-[10px] px-1.5 py-0.5 rounded hover:bg-white/10 text-stone-400 shrink-0"
            onClick={(e) => { e.stopPropagation(); onOpen() }}
            aria-label={`Open ${widget.title || widget.kind}`}
          >
            ⤢
          </button>
        </div>
      )}
      {isSection && widget.title && (
        <div className="px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-stone-500">{widget.title}</div>
      )}
      <div className={isSection ? 'h-full' : 'h-[calc(100%-1.75rem)]'}>
        {/* The widget as the app draws it, when that was captured. The
            structural render is still published and is what the list view and a
            screen reader read; this is only the likeness. */}
        {widget.captureAssetId ? (
          <CaptureFrame
            apiBase={apiBase}
            token={token}
            assetId={widget.captureAssetId}
            kind={widget.kind}
            title={widget.title}
          />
        ) : (
          <WidgetBody widget={widget} apiBase={apiBase} token={token} />
        )}
      </div>
    </div>
  )
}

function Controls({
  zoom,
  onZoom,
  onFit,
  onReset,
  linear,
  onToggleLinear
}: {
  zoom: number
  onZoom: (factor: number) => void
  onFit: () => void
  onReset: () => void
  linear: boolean
  onToggleLinear: () => void
}): JSX.Element {
  const btn = 'px-2 py-1 rounded hover:bg-white/10 text-stone-300 text-xs'
  return (
    <div className="absolute top-3 left-3 flex items-center gap-1 rounded-lg border border-white/10 bg-stone-900/85 backdrop-blur px-1 py-1">
      <button className={btn} onClick={() => onZoom(1 / 1.2)} aria-label="Zoom out">−</button>
      <span className="text-[10px] tabular-nums text-stone-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
      <button className={btn} onClick={() => onZoom(1.2)} aria-label="Zoom in">+</button>
      <span className="w-px h-4 bg-white/10 mx-0.5" />
      <button className={btn} onClick={onFit}>Fit</button>
      <button className={btn} onClick={onReset}>Reset</button>
      <span className="w-px h-4 bg-white/10 mx-0.5" />
      <button className={btn} onClick={onToggleLinear} aria-pressed={linear}>
        List
      </button>
    </div>
  )
}

function Minimap({
  bounds,
  widgets,
  cam,
  host,
  onJump
}: {
  bounds: PublicDeskProjectionV1['bounds']
  widgets: PublicWidget[]
  cam: Camera
  host: React.RefObject<HTMLDivElement | null>
  onJump: (c: Camera) => void
}): JSX.Element | null {
  const W = 160
  const H = 110
  if (bounds.width === 0 || bounds.height === 0) return null
  const k = Math.min(W / bounds.width, H / bounds.height)
  const el = host.current
  // The viewport rectangle in desk space, projected into minimap space.
  const view = el
    ? {
        x: (-cam.x / cam.zoom - bounds.x) * k,
        y: (-cam.y / cam.zoom - bounds.y) * k,
        w: (el.clientWidth / cam.zoom) * k,
        h: (el.clientHeight / cam.zoom) * k
      }
    : null

  return (
    <div
      className="absolute bottom-3 right-3 rounded-lg border border-white/10 bg-stone-900/85 backdrop-blur overflow-hidden cursor-pointer"
      style={{ width: W, height: H }}
      onClick={(e) => {
        const el2 = host.current
        if (!el2) return
        const r = e.currentTarget.getBoundingClientRect()
        const dx = (e.clientX - r.left) / k + bounds.x
        const dy = (e.clientY - r.top) / k + bounds.y
        onJump({ zoom: cam.zoom, x: el2.clientWidth / 2 - dx * cam.zoom, y: el2.clientHeight / 2 - dy * cam.zoom })
      }}
      role="button"
      aria-label="Desk overview — click to move the view"
    >
      {widgets.map((w) => (
        <div
          key={w.id}
          className="absolute bg-white/20 rounded-[1px]"
          style={{
            left: (w.rect.x - bounds.x) * k,
            top: (w.rect.y - bounds.y) * k,
            width: Math.max(1, w.rect.width * k),
            height: Math.max(1, w.rect.height * k)
          }}
        />
      ))}
      {view && (
        <div
          className="absolute border border-accent/80 pointer-events-none"
          style={{ left: view.x, top: view.y, width: view.w, height: view.h }}
        />
      )}
    </div>
  )
}

function FocusView({
  widget,
  apiBase,
  token,
  onClose
}: {
  widget: PublicWidget
  apiBase: string
  token: string
  onClose: () => void
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div
      className="absolute inset-0 z-30 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={widget.title || widget.kind}
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl h-[80%] rounded-xl border border-white/10 bg-stone-900 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <span className="text-sm truncate">{widget.title || widget.kind}</span>
          <button className="px-2 py-1 rounded hover:bg-white/10 text-stone-400" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <WidgetBody widget={widget} apiBase={apiBase} token={token} />
        </div>
      </div>
    </div>
  )
}

function LinearView({
  projection,
  apiBase,
  token,
  onClose
}: {
  projection: PublicDeskProjectionV1
  apiBase: string
  token: string
  onClose: () => void
}): JSX.Element {
  // Reading order follows the desk top-to-bottom, then left-to-right, which is
  // how a sighted reader scans it too.
  const ordered = [...projection.widgets].sort((a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x)
  return (
    <div className="absolute inset-0 z-20 bg-stone-950/95 overflow-auto p-6" role="region" aria-label="Desk contents as a list">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-400">{projection.title} — list view</h2>
          <button className="px-2 py-1 rounded hover:bg-white/10 text-stone-400 text-xs" onClick={onClose}>
            Back to desk
          </button>
        </div>
        <ol className="space-y-4">
          {ordered.map((w) => (
            <li key={w.id} className="rounded-lg border border-white/10 bg-stone-900/60">
              <div className="px-3 py-2 border-b border-white/10 text-xs text-stone-300">
                {w.title || w.kind}
              </div>
              <div className="max-h-72 overflow-auto">
                <WidgetBody widget={w} apiBase={apiBase} token={token} />
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
